import axios from "axios";
import { BASE_URL } from "../env";
import { summarizeLoanAllocation, toIstDateOnly } from "./loanAllocation";
import {
  getDisbursementAmount,
  isValidPaidReceipt,
  getEmiDay,
  getFirstAndLastEmiDates,
  resolveEffectiveStartDate,
  getEmiDatesInRange,
  calculateEmiAmount,
  getEffectiveEmi,
  rawMonthlyEmi,
  getMonthlyLoanTerms,
} from "./monthlyLoanTerms";

// Moved to monthlyLoanTerms.js so the backend foreclosure quote can use an identical copy;
// re-exported so every existing import from utils keeps working unchanged.
export {
  getDisbursementAmount,
  isValidPaidReceipt,
  getEmiDay,
  MIN_DAYS_TO_FIRST_EMI,
  getFirstAndLastEmiDates,
  resolveEffectiveStartDate,
  getEmiDatesInRange,
  calculateEmiAmount,
  getEffectiveEmi,
  getMonthlyLoanTerms,
} from "./monthlyLoanTerms";



/**
 * As above, but also inspects the linked collection approval row. Use this where
 * the approval association has been loaded; it additionally drops receipts whose
 * approval was retriggered or rejected by any role in the chain.
 */
export const isValidPaidReceiptWithApproval = (receipt, approval) => {
  if (!isValidPaidReceipt(receipt)) return false;
  if (!approval) return true;
  const voidedByApproval =
    approval.fieldManagerStatus === "retrigger" ||
    approval.branchManagerStatus === "retrigger" ||
    approval.misStatus === "retrigger" ||
    approval.accountManagerStatus === "retrigger" ||
    approval.branchManagerStatus === "rejected" ||
    approval.misStatus === "rejected" ||
    approval.accountManagerStatus === "rejected";
  return !voidedByApproval;
};


export const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === "") return 0;
  const stringAmount = String(amount).replace(/,/g, "");
  const num = parseFloat(stringAmount);
  if (isNaN(num)) return amount;
  return Math.round(num);
};

/**
 * Is this loan covered by insurance?
 *
 * One definition, shared by every screen that shows an insurance figure, so the
 * reports cannot disagree about the same loan.
 *
 * A loan counts as covered when the `isLoanInsured` flag is set, OR when an
 * insurance amount is recorded against it. The second half matters because the
 * flag is not always filled in: one Business Loan (1111VCT061767) has no flag
 * but does carry an insurance amount of Rs.3,426. The flag alone therefore
 * understates cover, which is why the rule is an OR and not a replacement -
 * 24 Business Loans and 8 GP Loans have the flag set with no amount recorded,
 * and those must stay covered.
 *
 * This replaces two broken gates (bugfix.md Bug 23): `(data.isLoanInsured || 1)`,
 * which is always true and so ignored the flag completely, and the Master
 * report's forced `isLoanInsured: 1`, which a later object spread silently
 * overwrote with the raw value.
 *
 * @param {object} data - a loan row
 * @returns {boolean}
 */
export const isLoanCovered = (data) =>
  Boolean(data?.isLoanInsured) || Number(data?.insuranceAmount) > 0;

/**
 * One definition of a report's date window, so the Master and Outstanding
 * screens cannot drift apart again.
 *
 * Every report that offers a From/To picker needs the same two rules:
 *   - the window covers whole days: 00:00:00.000 to 23:59:59.999. Bounding the
 *     end at midnight drops every record timestamped later that day. JLG
 *     Outstanding did exactly that and lost 8 loans worth Rs.3,20,000 against
 *     Master for a To-date that was a disbursement day (bugfix.md Bug 18).
 *   - `asAtDate` is the day balances are reported "as at", falling back to
 *     today when no date is chosen (bugfix.md Bug 16).
 *
 * @param {string|Date} fromValue - the From picker value, may be empty
 * @param {string|Date} toValue   - the To picker value, may be empty
 * @returns {{start: Date, end: Date, asAtDate: string}}
 */
export const getReportWindow = (fromValue, toValue) => {
  const start = fromValue ? new Date(fromValue) : new Date(0);
  start.setHours(0, 0, 0, 0);
  const end = toValue ? new Date(toValue) : new Date();
  end.setHours(23, 59, 59, 999);
  return {
    start,
    end,
    asAtDate: toValue || new Date().toISOString().split("T")[0],
  };
};

export function formatISODateToLocalDMY(isoDate) {
  if (!isoDate) return "-";

  // If it's a string, extract the date part directly to avoid timezone shifts
  if (typeof isoDate === "string") {
    const datePart = isoDate.split(/[T ]/)[0];
    if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split("-");
      return `${d}/${m}/${y}`;
    }
  }

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return isoDate;
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

const toLocalYmd = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};


export const getJlgFirstAndLastEmiDates = (
  disbursementDate,
  tenure,
  emiDayOrder
) => {
  // Without a meeting day there is no EMI schedule to work out. Returning
  // empty dates lets the caller show a blank cell. Previously this went
  // straight to .split() and threw "Cannot read properties of null (reading
  // 'split')", which took down the entire report page rather than one cell.
  if (!emiDayOrder || typeof emiDayOrder !== "string") {
    return { firstEmiDate: null, lastEmiDate: null, emiDates: [], nextEmiDate: null };
  }

  const [weekNumber, dayOfWeek] = emiDayOrder.split(",");
  const dayOfWeekMap = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 0,
  };
  const targetDay = dayOfWeekMap[dayOfWeek];
  const transactionDate = new Date(disbursementDate);
  const tenureMonths = parseInt(tenure) || 0;
  let firstEmiDate, lastEmiDate;

  for (let month = 1; month <= tenureMonths; month++) {
    const paymentDate = new Date(transactionDate.getFullYear(), transactionDate.getMonth() + month, 1);

    // Find the nth occurrence of the target day in the month
    let count = 0;
    const firstDayOfMonth = new Date(
      paymentDate.getFullYear(),
      paymentDate.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      paymentDate.getFullYear(),
      paymentDate.getMonth() + 1,
      0
    );

    for (
      let date = firstDayOfMonth;
      date <= lastDayOfMonth;
      date.setDate(date.getDate() + 1)
    ) {
      //   console.log("date.getDay(): " + date.getDay());
      if (date.getDay() === targetDay) {
        count++;
        if (count === parseInt(weekNumber)) {
          if (month === 1) firstEmiDate = date;
          if (month === tenure) lastEmiDate = date;
          break;
        }
      }
    }
  }

  return { firstEmiDate, lastEmiDate };
};

export const getJlgEmiDatesInRange = (
  emiDayOrder,
  fromDate,
  toDate,
  firstEmiDate,
  lastEmiDate
) => {
  const emiDates = [];
  const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

  let parsedFrom = fromDate ? new Date(fromDate) : null;
  let parsedTo = toDate ? new Date(toDate) : null;

  const from = isValidDate(parsedFrom) ? parsedFrom : (firstEmiDate ? new Date(firstEmiDate) : new Date(0));
  const to = isValidDate(parsedTo) ? parsedTo : (lastEmiDate ? new Date(lastEmiDate) : new Date());

  const [weekNumber, dayOfWeek] = emiDayOrder.split(",");
  const dayOfWeekMap = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 0,
  };
  const targetDay = dayOfWeekMap[dayOfWeek];
  const targetWeek = parseInt(weekNumber);

  // Helper function to check if date is within range
  const isDateInRange = (date) => date >= from && date <= to;

  // Start from the first day of the month containing 'from' date
  let currentMonthStart = new Date(from.getFullYear(), from.getMonth(), 1);
  // End at the last day of the month containing 'to' date
  let currentMonthEnd = new Date(to.getFullYear(), to.getMonth() + 1, 0);

  // Iterate through each month in the range
  while (currentMonthStart <= currentMonthEnd) {
    let count = 0;
    const month = currentMonthStart.getMonth();
    const year = currentMonthStart.getFullYear();
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Find the nth occurrence of the target day in this month
    for (
      let date = new Date(year, month, 1);
      date <= lastDayOfMonth;
      date.setDate(date.getDate() + 1)
    ) {
      if (date.getDay() === targetDay) {
        count++;
        if (count === targetWeek) {
          const emiDate = new Date(date);
          // Check if the date is within our range and between first and last EMI dates
          if (isDateInRange(emiDate)) {
            if (
              (!firstEmiDate || emiDate >= new Date(firstEmiDate)) &&
              (!lastEmiDate || emiDate <= new Date(lastEmiDate))
            ) {
              emiDates.push(new Date(emiDate));
            }
          }
          break;
        }
      }
    }

    // Move to next month
    currentMonthStart.setMonth(currentMonthStart.getMonth() + 1);
  }

  return emiDates;
};






/**
 * Same validation and zero-rate guard as calculateEmiAmount above, but
 * returns the UNROUNDED number instead of rounding to 2 decimal places.
 *
 * Added when consolidating the 24 other hand-written copies of this formula
 * across the app onto calculateEmiAmount: every one of those callers either
 * applied its own Math.round() for a whole rupee, used the raw value
 * directly in a repayment-schedule loop, or accumulated it across many
 * months in a report - none of them wanted calculateEmiAmount's 2-decimal
 * rounding as an intermediate step. Routing them through calculateEmiAmount
 * and then rounding AGAIN is not equivalent to rounding the raw number once:
 * a value like 1815.4997 rounds to 1815 directly, but rounds to 1815.50 at
 * 2 decimals first, which then rounds UP to 1816 - a real, visible one-rupee
 * difference for any loan whose raw EMI happens to land within about three
 * paise of a whole-rupee boundary. This function exists so every one of
 * those callers keeps its exact pre-consolidation output. The 11 screens
 * that already called calculateEmiAmount before this file was touched are
 * untouched and keep using it exactly as they did.
 */
export const calculateEmiAmountRaw = (sanctionedAmount, rateOfInterest, tenureInMonths) => {
  try {
    const amount = parseFloat(sanctionedAmount);
    const rate = parseFloat(rateOfInterest);
    const tenure = parseInt(tenureInMonths);

    if (!amount || amount <= 0 || rate < 0 || !tenure || tenure <= 0) {
      return 0;
    }

    if (rate === 0) {
      return amount / tenure;
    }

    const { emi: emiAmount, denominator } = rawMonthlyEmi(amount, rate, tenure);

    if (denominator === 0) {
      return amount / tenure;
    }

    return emiAmount;
  } catch (error) {
    console.error('Error calculating raw EMI amount:', error);
    return 0;
  }
};

export const calculateInsuranceAmount = (loanAmount, tenureInMonths) => {
  try {
    const amount = parseFloat(loanAmount) || 0;
    const tenure = parseInt(tenureInMonths) || 0;

    if (amount <= 0 || tenure <= 0) return 0;

    if (amount > 200000) {
      // New formula for loans > 200,000: 1.392% annual rate + 18% GST
      const basePremium = amount * (1.392 * (tenure / 12)) / 100;
      const gst = basePremium * 0.18;
      const totalPremium = Math.round(basePremium + gst);
      return totalPremium;
    } else {
      // Standard formula: 1.142% annual rate
      const insuranceAmount = Math.round(amount * (1.142 * (tenure / 12)) / 100);
      return insuranceAmount;
    }
  } catch (error) {
    console.error("Error calculating insurance amount:", error);
    return 0;
  }
};

/**
 * The amount above which a GPL loan attracts the higher insurance rate and the
 * insurance is quoted inclusive of its own GST. A loan of exactly this amount
 * counts as high value.
 */
export const GPL_HIGH_VALUE_THRESHOLD = 200000;

/**
 * The GPL charge sheet, in one place.
 *
 * Two rules matter here and they were previously spread over four calculators
 * that disagreed with each other and with the labels on screen (bugfix.md
 * Bug 25):
 *
 *   1. Insurance keys off the TOTAL loan amount - gold eligibility plus any cash
 *      loan - never the gold eligibility alone. Only one rate ever applies; the
 *      two rates are never added together.
 *   2. At or above the threshold the insurance is quoted INCLUSIVE of its own
 *      GST, so the insurance figure is premium + GST on that premium. The fee
 *      GST stays on the processing fee alone, always. Below the threshold the
 *      insurance carries no GST at all.
 *
 * Rule 2 matches how Business Loan insurance is already reported by
 * calculateInsuranceAmount above, so the two products quote a premium the same
 * way, and it matches the client's own worked sheet.
 *
 * This is the fee GST only. The gold GST that feeds the eligible amount
 * (gstByBm) is a different charge and is untouched by this function.
 *
 * Rounding: the processing fee and the fee GST round exactly as they did before
 * Bug 25. The GST-inclusive insurance rounds ONCE at the end, on premium + GST
 * together, so it does not drift a rupee above the true figure.
 *
 * TENURE SCALING (Bug 27). The insurance premium is scaled by the tenure, the
 * same way Business Loan already scales it. The divisor is TWENTY-FOUR, not
 * twelve, because the percentage stored on the funding agency (2.284% standard,
 * 2.784% above the threshold) is a TWO-YEAR rate, not an annual one - which is
 * why calculateInsuranceAmount and the BL calculator both divide by 24.
 *
 * Getting that divisor wrong doubles every premium on the commonest loan in the
 * book. With /24 a 24-month loan scales by exactly 1.0, so the client's signed
 * worked sheet (Rs.2,20,000 -> insurance Rs.7,227) is unchanged, and 904 of the
 * 970 disbursed GP Loans compute precisely as before.
 *
 * The processing fee, the fee GST and the security deposit are NOT scaled -
 * they are one-off charges, not a premium bought by the year.
 */
export const GPL_INSURANCE_BASE_TENURE = 24;

/**
 * Shared insurance-above-threshold calculation for GPL, BL and JLG (bugfix.md
 * Bug 57). All three products charge the same SHAPE of rule - a higher flat
 * rate above Rs.2,00,000, scaled by tenure against a 24-month base, with GST
 * added on top of the premium only above that threshold - but each product
 * reads its own funding-agency columns (gpl/bl/jlg) and, until this fix,
 * BL had no GST step at all and JLG had neither the GST step nor the tenure
 * scaling. One function now backs all three call sites instead of three (in
 * JLG's case, four - it duplicated its own calculation twice in one file)
 * separately-maintained copies.
 *
 * The threshold is now STRICTLY greater than Rs.2,00,000 - a loan of exactly
 * that amount gets the standard rate. All three products previously included
 * exactly Rs.2,00,000 in the higher tier (GPL and BL via `>=`, JLG via
 * `> 199999`, the same thing for whole-rupee amounts); this was a confirmed
 * correction to all three, not only a BL/JLG catch-up to GPL's old boundary.
 *
 * `prefix` selects which agency columns to read: "gpl", "bl" or "jlg", each
 * following the same four-column shape -
 * `{prefix}InsuranceEnabled`/`{prefix}InsurancePercentage` (standard rate) and
 * `{prefix}InsuranceAbove199999Enabled`/`{prefix}InsuranceAbove199999Percentage`
 * (high-value rate) - plus that product's own `{prefix}GstEnabled`/
 * `{prefix}GstPercentage` for the GST added above the threshold, matching
 * exactly how GPL already used its own GST columns for this before this fix.
 */
export const INSURANCE_HIGH_VALUE_THRESHOLD = 200000;

export function computeInsuranceAboveThreshold(loanAmount, tenureInMonths, agency, prefix) {
  const amount = Number(loanAmount) || 0;
  const isHighValue = amount > INSURANCE_HIGH_VALUE_THRESHOLD;

  const tenure = Number(tenureInMonths);
  const tenureFactor =
    Number.isFinite(tenure) && tenure > 0 ? tenure / GPL_INSURANCE_BASE_TENURE : 1;

  let insuranceRate = 0;
  if (isHighValue && agency?.[`${prefix}InsuranceAbove199999Enabled`]) {
    insuranceRate = Number(agency[`${prefix}InsuranceAbove199999Percentage`]) || 0;
  } else if (agency?.[`${prefix}InsuranceEnabled`]) {
    insuranceRate = Number(agency[`${prefix}InsurancePercentage`]) || 0;
  }

  const gstRate = Number(agency?.[`${prefix}GstPercentage`]) || 0;
  const gstEnabled = isHighValue && Boolean(agency?.[`${prefix}GstEnabled`]);

  const premium = amount * (insuranceRate / 100) * tenureFactor;
  const insuranceGst = gstEnabled ? premium * (gstRate / 100) : 0;
  const insurance = Math.round(premium + insuranceGst);

  // The stored/charged total is rounded once, from premium + GST together, so
  // it never drifts a rupee high. The split below is for display only, with
  // the GST half taken as the remainder so the two always add back to the
  // total exactly - rounding each half independently leaves them a rupee
  // apart about a quarter of the time.
  const displayPremium = Math.round(premium);
  const displayGst = insurance - displayPremium;

  return {
    isHighValue,
    tenureFactor,
    insuranceRate,
    insuranceEffectiveRate: insuranceRate * tenureFactor,
    insurance,
    insurancePremium: displayPremium,
    insuranceGst: displayGst,
    insuranceIncludesGst: insuranceGst > 0,
  };
}

export const computeGplCharges = ({
  eligibleAmount = 0,
  cashLoanAmount = 0,
  agency = null,
  tenureInMonths = GPL_INSURANCE_BASE_TENURE,
} = {}) => {
  const cashComponent = Number(cashLoanAmount || 0);
  const totalLoanAmount = Number(eligibleAmount || 0) + cashComponent;

  // A gold-only loan hands the customer no cash, so there is nothing to net the
  // charges out of - the caller must not subtract them from the release amount.
  const hasCashComponent = cashComponent > 0;

  // No funding agency on the loan: fall back to the same defaults used before,
  // via a synthetic agency object so the shared helper still applies the same
  // threshold/tenure/GST rule rather than a fourth hand-written copy of it.
  const effectiveAgency = agency || {
    gplInsuranceEnabled: true,
    gplInsurancePercentage: 1.142,
    gplInsuranceAbove199999Enabled: true,
    gplInsuranceAbove199999Percentage: 1.142,
    gplGstEnabled: true,
    gplGstPercentage: 18,
  };

  const insuranceResult = computeInsuranceAboveThreshold(
    totalLoanAmount,
    tenureInMonths,
    effectiveAgency,
    "gpl"
  );
  const { isHighValue, tenureFactor, insuranceRate, insuranceEffectiveRate, insurance, insurancePremium, insuranceGst, insuranceIncludesGst } = insuranceResult;

  if (!agency) {
    const processingFee = Math.round(totalLoanAmount * 0.02);
    return {
      totalLoanAmount,
      isHighValue,
      hasCashComponent,
      tenureFactor,
      processingFee,
      insurance,
      insurancePremium,
      insuranceGst,
      insuranceIncludesGst,
      insuranceRate,
      insuranceEffectiveRate,
      gst: Math.round(processingFee * 0.18),
      gstRate: 18,
      securityDeposit1: Math.round(totalLoanAmount * 0.01),
    };
  }

  const processingFee = agency.gplProcessingFeeEnabled
    ? Math.round(totalLoanAmount * (Number(agency.gplProcessingFeePercentage) / 100))
    : 0;

  const gstRate = Number(agency.gplGstPercentage) || 0;
  const gstEnabled = Boolean(agency.gplGstEnabled);

  // The fee GST is on the processing fee alone. Neither the insurance nor the
  // security deposit is part of its base - the insurance GST is computed
  // above, inside computeInsuranceAboveThreshold.
  const gst = gstEnabled ? Math.round(processingFee * (gstRate / 100)) : 0;

  const securityDeposit1 = agency.gplSecurityDepositEnabled
    ? Math.round(totalLoanAmount * (Number(agency.gplSecurityDepositPercentage) / 100))
    : 0;

  return {
    totalLoanAmount,
    isHighValue,
    hasCashComponent,
    tenureFactor,
    processingFee,
    insurance,
    insurancePremium,
    insuranceGst,
    insuranceIncludesGst,
    // The rate as configured on the agency, and the rate actually charged after
    // tenure scaling. The screen must show the second one or its own label
    // contradicts its own arithmetic.
    insuranceRate,
    insuranceEffectiveRate,
    gst,
    gstRate,
    securityDeposit1,
  };
};


// Helper function to calculate EMI breakdown into principal and interest components
const calculatePrincipalAndInterest = (remainingBalance, emiAmount, rateOfInterest) => {
  try {
    const balance = parseFloat(remainingBalance) || 0;
    const emi = parseFloat(emiAmount) || 0;
    const rate = parseFloat(rateOfInterest) || 0;

    // Validate inputs
    if (balance <= 0 || emi <= 0 || rate < 0) {
      return { principal: 0, interest: 0 };
    }

    // Handle zero interest rate
    if (rate === 0) {
      return {
        principal: Math.min(emi, balance),
        interest: 0
      };
    }

    const monthlyInterestRate = rate / 12 / 100;
    const calculatedInterest = balance * monthlyInterestRate;

    // Round interest first
    const roundedInterest = Math.max(0, Math.round(calculatedInterest));

    // Calculate principal as EMI - interest to ensure they sum to EMI
    const calculatedPrincipal = emi - roundedInterest;

    // Ensure principal doesn't exceed remaining balance and is non-negative
    const roundedPrincipal = Math.max(0, Math.min(Math.round(calculatedPrincipal), balance));

    // If principal was capped by balance, recalculate interest to maintain sum
    const finalInterest = roundedPrincipal < Math.round(calculatedPrincipal)
      ? Math.round(emi - roundedPrincipal)
      : roundedInterest;

    return {
      principal: roundedPrincipal,
      interest: Math.max(0, finalInterest)
    };
  } catch (error) {
    console.error('Error calculating principal and interest:', error);
    return { principal: 0, interest: 0 };
  }
};

// Function to calculate adjusted balance up to a specific date
const getAdjustedRemainingBalance = (data, emiAmount, rateOfInterest, firstEmiDate, fromDate) => {
  try {
    let remainingBalance = getDisbursementAmount(data);

    if (remainingBalance <= 0 || !fromDate || !firstEmiDate) {
      return remainingBalance;
    }

    let currentEmiDate = new Date(firstEmiDate);
    const targetDate = new Date(fromDate);

    // Validate dates
    if (isNaN(currentEmiDate.getTime()) || isNaN(targetDate.getTime())) {
      return remainingBalance;
    }

    let iterationCount = 0;
    const maxIterations = 1000; // Prevent infinite loops

    while (currentEmiDate < targetDate && remainingBalance > 0 && iterationCount < maxIterations) {
      const { principal } = calculatePrincipalAndInterest(
        remainingBalance,
        emiAmount,
        rateOfInterest
      );

      remainingBalance = Math.max(0, remainingBalance - principal);
      // Move to the next month safely
      currentEmiDate = new Date(currentEmiDate.getFullYear(), currentEmiDate.getMonth() + 1, 1);
      iterationCount++;
    }

    return remainingBalance;
  } catch (error) {
    console.error('Error calculating adjusted remaining balance:', error);
    return getDisbursementAmount(data);
  }
};



const getAdjustedRemainingBalanceGpl = (data, emiAmount, rateOfInterest, firstEmiDate, fromDate) => {
  try {
    let remainingBalance = getDisbursementAmount(data);

    if (remainingBalance <= 0 || !fromDate || !firstEmiDate) {
      return remainingBalance;
    }

    let currentEmiDate = new Date(firstEmiDate);
    const targetDate = new Date(fromDate);

    // Validate dates
    if (isNaN(currentEmiDate.getTime()) || isNaN(targetDate.getTime())) {
      return remainingBalance;
    }

    let iterationCount = 0;
    const maxIterations = 1000; // Prevent infinite loops

    while (currentEmiDate < targetDate && remainingBalance > 0 && iterationCount < maxIterations) {
      const { principal } = calculatePrincipalAndInterest(
        remainingBalance,
        emiAmount,
        rateOfInterest
      );

      remainingBalance = Math.max(0, remainingBalance - principal);
      // Move to the next month safely
      currentEmiDate = new Date(currentEmiDate.getFullYear(), currentEmiDate.getMonth() + 1, 1);
      iterationCount++;
    }

    return remainingBalance;
  } catch (error) {
    console.error('Error calculating adjusted remaining balance for GPL:', error);
    return getDisbursementAmount(data);
  }
};

export const generateJlgRowsWithEmiDates = (data, fromDate, toDate) => {
  if (
    data.branchManagerStatus === "disbursed" &&
    data.branchManagerStatusUpdatedAt
  ) {
    const { firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
      data.branchManagerStatusUpdatedAt,
      data.proposedLoanDetails.tenureInMonths,
      data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
    );

    const emiDates = getJlgEmiDatesInRange(
      data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
      fromDate,
      toDate,
      firstEmiDate,
      lastEmiDate
    );

    const emiAmount = Math.round(calculateEmiAmount(
      data.sanctionedLoanAmountBySanctionCommittee,
      data.proposedLoanDetails.rateOfInterest,
      data.proposedLoanDetails.tenureInMonths
    ));

    const emiMonthsPaid = data.receiptsDetails
      ? new Set(
        data.receiptsDetails
          .filter((receipt) => receipt.emiDate <= toDate && isValidPaidReceipt(receipt))
          .map((receipt) => receipt.emiDate)
      ).size
      : 0;

    // Adjust remaining balance based on EMIs before fromDate
    let remainingBalance = getAdjustedRemainingBalance(
      data,
      emiAmount,
      data.proposedLoanDetails.rateOfInterest,
      firstEmiDate,
      fromDate
    );

    const result = emiDates.map((emiDate) => {
      const { principal, interest } = calculatePrincipalAndInterest(
        remainingBalance,
        emiAmount,
        data.proposedLoanDetails.rateOfInterest
      );

      remainingBalance -= principal;

      return {
        ...data,
        formattedEmiDateByBranchManager: emiDate,
        emiAmount, // EMI amount for each row
        emiMonthsPaid, // Total EMI months paid up to the toDate
        principal, // Principal portion of the current EMI
        interest, // Interest portion of the current EMI
      };
    });

    // Log the result before returning
    // console.log("EMI Data:", JSON.stringify(result));

    return result;
  }
  return []; // No EMI dates to display if not disbursed
};

export const generateRowsWithEmiDates = (data, fromDate, toDate) => {
  // Add validation checks
  if (
    !data ||
    data.branchManagerStatus !== "disbursed" ||
    !data.branchManagerStatusUpdatedAt ||
    !data.proposedLoanDetails ||
    !data.proposedLoanDetails.tenureInMonths ||
    data.proposedLoanDetails.rateOfInterest < 0
  ) {
    return [];
  }

  try {
    const emiDay = getEmiDay(data);
    const { firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
      data.branchManagerStatusUpdatedAt,
      data.proposedLoanDetails.tenureInMonths,
      emiDay,
      resolveEffectiveStartDate(data)
    );

    if (!firstEmiDate || !lastEmiDate) {
      return [];
    }

    const emiDates = getEmiDatesInRange(
      emiDay,
      fromDate,
      toDate,
      firstEmiDate,
      lastEmiDate
    );

    if (!emiDates || emiDates.length === 0) {
      return [];
    }

    // Choose EMI calculation amount based on loan type
    const loanAmountForEmi = getDisbursementAmount(data);

    // Validate loan amount
    if (loanAmountForEmi <= 0) {
      console.warn(`Invalid loan amount for customer ${data.customerId}: ${loanAmountForEmi}`);
      return [];
    }

    // Calculate EMI amount using the prioritized logic (Stored EMI > Re-calculated Gross EMI)
    const emiAmount = getEffectiveEmi(data);

    if (emiAmount <= 0) {
      console.warn(`Invalid EMI amount for customer ${data.customerId}: ${emiAmount}`);
      return [];
    }

    const emiMonthsPaid = data.receiptsDetails
      ? new Set(
        data.receiptsDetails
          .filter((receipt) => receipt.emiDate <= toDate && isValidPaidReceipt(receipt))
          .map((receipt) => receipt.emiDate)
      ).size
      : 0;

    // Get remaining balance based on loan type
    let remainingBalance;
    if (data.loanType === "GP Loan") {
      remainingBalance = getAdjustedRemainingBalanceGpl(
        data,
        emiAmount,
        data.proposedLoanDetails.rateOfInterest,
        firstEmiDate,
        fromDate
      );
    } else {
      remainingBalance = getAdjustedRemainingBalance(
        data,
        emiAmount,
        data.proposedLoanDetails.rateOfInterest,
        firstEmiDate,
        fromDate
      );
    }

    // Ensure remaining balance is not negative at start
    if (remainingBalance <= 0) {
      console.warn(`Non-positive remaining balance for customer ${data.customerId}: ${remainingBalance}`);
      return [];
    }

    // Prepare EMI amount for calculations: floor for Business Loan, round for others
    // Use this rounded amount for ALL calculations to ensure consistency
    const emiAmountToUse = Math.round(emiAmount);

    const result = emiDates.map((emiDate, index) => {
      // Calculate principal and interest using the rounded EMI amount
      const { principal, interest } = calculatePrincipalAndInterest(
        remainingBalance,
        emiAmountToUse,
        data.proposedLoanDetails.rateOfInterest
      );

      // Update remaining balance for next iteration
      remainingBalance = Math.max(0, remainingBalance - principal);

      return {
        ...data,
        formattedEmiDateByBranchManager: emiDate,
        // Keep raw EMI for reference and debugging, and provide integer amount per loan type
        emiAmountRaw: Math.round(emiAmount * 100) / 100,
        emiAmount: emiAmountToUse,
        emiMonthsPaid,
        principal: Math.max(0, principal), // Ensure non-negative
        interest: Math.max(0, interest), // Ensure non-negative
      };
    });

    return result;
  } catch (error) {
    console.error(`Error generating rows for customer ${data.customerId}:`, error);
    return [];
  }
};

export const generateTotalPendingAmountForMember = (data, fromDate, toDate) => {
  // Check if disbursement date exists and calculate first/last EMI date
  if (
    data.branchManagerStatus === "disbursed" &&
    data.branchManagerStatusUpdatedAt &&
    data.loanStatus?.toLowerCase() !== "foreclosed" &&
    data.loanStatus?.toLowerCase() !== "completed"
  ) {
    let firstEmiDate, lastEmiDate, emiDates;

    const emiDay = getEmiDay(data);
    if (data.loanType === "Business Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "JLG Loan") {
      ({ firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
      ));
      emiDates = getJlgEmiDatesInRange(
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "GP Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else {
      return []; // Return empty array for unknown loan types
    }

    // Choose correct loan amount based on loan type with proper validation
    let sanctionedLoanAmount;
    if (data.loanType === "GP Loan") {
      sanctionedLoanAmount = getDisbursementAmount(data);
    } else {
      sanctionedLoanAmount = data.sanctionedLoanAmountBySanctionCommittee;
    }

    // Validate and convert parameters to numbers
    const loanAmount = parseFloat(sanctionedLoanAmount);
    const rateOfInterest = parseFloat(data.proposedLoanDetails?.rateOfInterest);
    const tenureInMonths = parseInt(data.proposedLoanDetails?.tenureInMonths);

    // Check if all required parameters are valid numbers
    if (isNaN(loanAmount) || isNaN(rateOfInterest) || isNaN(tenureInMonths)) {
      console.error('Invalid EMI calculation parameters:', {
        originalLoanAmount: sanctionedLoanAmount,
        parsedLoanAmount: loanAmount,
        originalRateOfInterest: data.proposedLoanDetails?.rateOfInterest,
        parsedRateOfInterest: rateOfInterest,
        originalTenureInMonths: data.proposedLoanDetails?.tenureInMonths,
        parsedTenureInMonths: tenureInMonths,
        loanType: data.loanType,
        dataId: data.id || data.ApplicationId || 'unknown'
      });
      return null; // Skip this record if parameters are invalid
    }

    // Additional validation
    if (loanAmount <= 0 || rateOfInterest < 0 || tenureInMonths <= 0) {
      console.error('Invalid EMI calculation values:', {
        loanAmount,
        rateOfInterest,
        tenureInMonths,
        dataId: data.id || 'unknown'
      });
      return null;
    }

    // Calculate EMI amount with validated parameters
    const emiAmount = calculateEmiAmount(loanAmount, rateOfInterest, tenureInMonths);

    // Check if EMI calculation returned a valid number
    if (isNaN(emiAmount) || emiAmount <= 0) {
      console.error('Invalid EMI amount calculated:', {
        emiAmount,
        loanAmount,
        rateOfInterest,
        tenureInMonths,
        dataId: data.id || 'unknown'
      });
      return null;
    }

    // Initialize total pending amount and track the first fully pending EMI date
    let totalPendingAmount = 0;
    let firstPendingEmiDate = null;

    // Ensure receiptsDetails exists and is an array
    const receiptsDetails = Array.isArray(data.receiptsDetails) ? data.receiptsDetails : [];

    // Loop through all EMI dates and calculate total pending amount
    emiDates.forEach((emiDate) => {
      const formattedEmiDate = `${emiDate.getFullYear()}-${String(
        emiDate.getMonth() + 1
      ).padStart(2, "0")}-${String(emiDate.getDate()).padStart(2, "0")}`;

      // Get receipts for this emiDate
      const receiptsForEmiDate = receiptsDetails.filter(
        (receipt) =>
          receipt.emiDate === formattedEmiDate && isValidPaidReceipt(receipt)
      );

      // Calculate total received amount
      const totalReceivedAmount = receiptsForEmiDate.reduce(
        (sum, receipt) => {
          const receivedAmount = parseFloat(receipt.receivedAmount) || 0;
          return sum + receivedAmount;
        },
        0
      );

      // Calculate pending amount for this EMI
      let pendingAmount = 0;
      if (receiptsForEmiDate.length === 0) {
        pendingAmount = emiAmount; // No receipt => full EMI pending
      } else if (totalReceivedAmount < emiAmount) {
        pendingAmount = emiAmount - totalReceivedAmount; // Partial payment
      }

      if (pendingAmount > 0 && !firstPendingEmiDate) {
        firstPendingEmiDate = emiDate;
      }

      totalPendingAmount += Math.round(pendingAmount);
    });

    // Calculate overdue days
    let overdueDays = 0;
    if (firstPendingEmiDate) {
      const currentDate = new Date();
      const timeDiff = currentDate - firstPendingEmiDate;
      overdueDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    }

    if (totalPendingAmount > 0) {
      return {
        ...data,
        totalPendingAmount,
        status: "Pending",
        overdueDays,
      };
    }
  }

  return null; // No pending amount or not disbursed
};

export const generateOutstandingAmountsForParMember = (
  data,
  fromDate,
  toDate
) => {
  // Check if the loan has been disbursed and has an updated status
  if (
    data.branchManagerStatus === "disbursed" &&
    data.branchManagerStatusUpdatedAt &&
    data.loanStatus?.toLowerCase() !== "foreclosed" &&
    data.loanStatus?.toLowerCase() !== "completed"
  ) {
    let firstEmiDate, lastEmiDate, emiDates;

    const emiDay = getEmiDay(data);
    if (data.loanType === "Business Loan" || data.loanType === "GP Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "JLG Loan") {
      ({ firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
      ));
      emiDates = getJlgEmiDatesInRange(
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else {
      return [];
    }

    // Use unified disbursement amount
    const loanAmount = getDisbursementAmount(data);

    const emiAmount = Math.round(
      calculateEmiAmount(
        loanAmount,
        data.proposedLoanDetails.rateOfInterest,
        data.proposedLoanDetails.tenureInMonths
      )
    );

    let totalOutstandingPrincipal = Math.round(loanAmount);
    let totalOutstandingInterest = 0;
    let firstMissedEmiDate = null;
    let hasMissedEmi = false;
    let overduePrincipal = 0;
    let overdueInterest = 0;


    emiDates.forEach((emiDate) => {
      const formattedEmiDate = `${emiDate.getFullYear()}-${String(
        emiDate.getMonth() + 1
      ).padStart(2, "0")}-${String(emiDate.getDate()).padStart(2, "0")}`;


      const receiptsForEmiDate = data.receiptsDetails.filter(
        (receipt) =>
          receipt.emiDate === formattedEmiDate && isValidPaidReceipt(receipt)
      );


      let totalReceivedAmount = Math.round(
        receiptsForEmiDate.reduce(
          (sum, receipt) => sum + receipt.receivedAmount,
          0
        )
      );


      const interestComponent = Math.round(
        (totalOutstandingPrincipal * data.proposedLoanDetails.rateOfInterest) /
        12 /
        100
      );


      if (totalReceivedAmount < emiAmount) {
        hasMissedEmi = true;

        const currentOverdue = emiAmount - totalReceivedAmount;
        if (totalReceivedAmount >= interestComponent) {
          overduePrincipal += currentOverdue;
        } else {
          overdueInterest += (interestComponent - totalReceivedAmount);
          overduePrincipal += (emiAmount - interestComponent);
        }

        // Set the first missed EMI date if it's not already set
        if (!firstMissedEmiDate) {
          firstMissedEmiDate = emiDate;
        }
      }

      // Calculate outstanding amounts
      if (totalReceivedAmount >= interestComponent) {
        // Deduct interest first
        totalOutstandingInterest += interestComponent; // Add this month's interest to total outstanding interest
        totalReceivedAmount -= interestComponent; // Remaining amount after paying interest

        // Deduct any remaining amount from the principal.
        // Clamped with Math.min so an overpayment (early settlement, advance EMI, or
        // month-on-month rounding drift) can never push the principal below zero.
        const principalReduction = Math.min(
          totalOutstandingPrincipal,
          Math.round(totalReceivedAmount)
        );
        totalOutstandingPrincipal -= principalReduction;
      } else {
        // If the received amount doesn't cover the interest, just add to the outstanding interest
        totalOutstandingInterest += Math.round(
          interestComponent - totalReceivedAmount
        ); // Remaining interest due
      }
    });

    // Calculate overdue days if there's a missed EMI date
    let overdueDays = 0;
    if (firstMissedEmiDate) {
      const currentDate = new Date();
      const timeDiff = currentDate - firstMissedEmiDate;
      overdueDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    }

    // Return only if any EMI has been missed
    if (hasMissedEmi) {
      // Floor every returned figure at 0. This function previously had no clamps at
      // all, so an overpayment or rounding drift could surface as a negative
      // outstanding or overdue amount on the PAR report. The sibling functions
      // (generateOutstandingAmountsForAllMember, generateLoanSummaryForMember)
      // have always clamped; this now matches them.
      return {
        ...data,
        totalOutstandingPrincipal: Math.max(0, Math.round(totalOutstandingPrincipal)),
        totalOutstandingInterest: Math.max(0, Math.round(totalOutstandingInterest)),
        overduePrincipal: Math.max(0, Math.round(overduePrincipal)),
        overdueInterest: Math.max(0, Math.round(overdueInterest)),
        overdueDays: Math.max(0, overdueDays), // Include overdue days
      };
    }
  }

  return null; // No outstanding amount or not disbursed
};


export const generateOutstandingAmountsForAllMember = (
  data,
  fromDate,
  toDate
) => {
  // Check if the loan has been disbursed and has an updated status
  if (
    data.branchManagerStatus === "disbursed" &&
    data.branchManagerStatusUpdatedAt &&
    data.loanStatus !== "foreclosed" &&
    data.loanStatus !== "completed"
  ) {
    let firstEmiDate, lastEmiDate, emiDates;
    const emiDay = getEmiDay(data);
    if (data.loanType === "Business Loan" || data.loanType === "GP Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
    } else if (data.loanType === "JLG Loan") {
      ({ firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
      ));
    } else {
      return null;
    }

    if (!firstEmiDate || !lastEmiDate) return null;

    // Choose the full range for EMI calculation to capture ALL receipts (including future ones).
    // JLG repayments fall on a weekly centre meeting ("Week No:2,Thursday"), not a day-of-month,
    // so they need getJlgEmiDatesInRange. Passing that text to getEmiDatesInRange made it return
    // an empty schedule, so no receipt was ever matched and every JLG loan reported its full
    // sanctioned amount as outstanding with zero paid.
    emiDates =
      data.loanType === "JLG Loan"
        ? getJlgEmiDatesInRange(
            data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
            firstEmiDate,
            lastEmiDate,
            firstEmiDate,
            lastEmiDate
          )
        : getEmiDatesInRange(
            emiDay,
            firstEmiDate,
            lastEmiDate,
            firstEmiDate,
            lastEmiDate
          );

    // Determine the sanctioned loan amount
    const sanctionedLoanAmount = getDisbursementAmount(data);

    const totalLoanInterest = calculateTotalLoanInterest(
      sanctionedLoanAmount,
      data.proposedLoanDetails.rateOfInterest,
      data.proposedLoanDetails.tenureInMonths
    );

    let totalOutstandingPrincipal = Math.round(sanctionedLoanAmount);
    let totalOutstandingInterest = Math.round(totalLoanInterest);
    let principalPaid = 0;
    let interestPaid = 0;
    let totalEmiPaid = 0;

    const processedReceiptIds = new Set();
    const allPaidReceipts = (data.receiptsDetails || []).filter(
      (r) =>
        (r.memberId === data.id || r.memberId === data.ApplicationId) &&
        isValidPaidReceipt(r)
    );

    // Loop through all EMI dates and calculate outstanding principal and interest
    emiDates.forEach((emiDate) => {
      // Use the LOCAL calendar date, not toISOString(). EMI dates are built with
      // new Date(y, m, d) = local midnight, and toISOString() converts to UTC, which
      // in IST (UTC+5:30) rolls the date back one day. The receipt date then never
      // matched, so no payment was ever applied. generateLoanSummaryForMember has
      // always normalised both sides with toLocalYmd; this now does the same.
      const formattedEmiDate = toLocalYmd(emiDate);

      // Get receipts for this emiDate
      const receiptsForEmiDate = allPaidReceipts.filter((receipt) => {
        const receiptDate =
          typeof receipt.emiDate === "string"
            ? toLocalYmd(new Date(receipt.emiDate))
            : toLocalYmd(receipt.emiDate);
        return receiptDate === formattedEmiDate;
      });

      // Calculate total received amount for this EMI date
      let totalReceivedAmount = Math.round(
        receiptsForEmiDate.reduce((sum, receipt) => {
          processedReceiptIds.add(receipt.id);
          return sum + (receipt.receivedAmount || 0);
        }, 0)
      );

      totalEmiPaid += totalReceivedAmount;

      // Calculate interest component for this EMI date (Monthly reducing balance)
      const interestComponent = Math.round(
        (totalOutstandingPrincipal * (data.proposedLoanDetails.rateOfInterest || 0)) / 12 / 100
      );

      // Calculate outstanding amounts
      if (totalReceivedAmount >= interestComponent) {
        // Deduct interest first
        interestPaid += interestComponent;
        totalOutstandingInterest -= interestComponent;
        totalReceivedAmount -= interestComponent;

        // Deduct remaining amount from the principal
        const principalReduction = Math.min(
          totalOutstandingPrincipal,
          totalReceivedAmount
        );
        principalPaid += principalReduction;
        totalOutstandingPrincipal -= principalReduction;
      } else {
        // If the received amount doesn't cover the interest
        interestPaid += totalReceivedAmount;
        totalOutstandingInterest -= totalReceivedAmount;
      }
    });


    return {
      ...data,
      totalOutstandingPrincipal: Math.max(0, Math.round(totalOutstandingPrincipal)),
      totalOutstandingInterest: Math.max(0, Math.round(totalOutstandingInterest)),
      // Paid figures were previously unclamped: a negative receipt would have made
      // them negative on the report. Floored for the same reason as the balances above.
      principalPaid: Math.max(0, Math.round(principalPaid)),
      interestPaid: Math.max(0, Math.round(interestPaid)),
      totalEmiPaid: Math.max(0, Math.round(totalEmiPaid)),
    };
  }

  return null; // No outstanding amount or not disbursed
};

export const generateRowsWithEmiAndCollectedAmounts = (
  data,
  fromDate,
  toDate
) => {
  if (
    data.branchManagerStatus === "disbursed" &&
    data.branchManagerStatusUpdatedAt
  ) {
    let firstEmiDate, lastEmiDate, emiDates;

    const emiDay = getEmiDay(data);
    if (
      data.loanType === "Business Loan" ||
      data.loanType === "GP Loan"
    ) {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "JLG Loan") {
      ({ firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
      ));
      emiDates = getJlgEmiDatesInRange(
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else {
      return []; // unknown loan type
    }

    // Select loan amount based on loan type
    const emiAmount = getEffectiveEmi(data);

    // Map each EMI date to a row
    const result = emiDates.map((emiDate) => {
      const formattedEmiDate = emiDate.toLocaleDateString("en-CA");

      const matchingReceipts = data.receiptsDetails?.filter(
        (receipt) =>
          receipt.memberId === data.id &&
          receipt.emiDate === formattedEmiDate &&
          isValidPaidReceipt(receipt)
      );

      const collectedEmiAmount = matchingReceipts?.reduce(
        (total, receipt) => total + (receipt.receivedAmount || 0),
        0
      );

      const collectedDates = matchingReceipts
        ?.map((receipt) => {
          const collectedDate = new Date(receipt.collectedDate).toLocaleString(
            "en-US",
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }
          );
          return `${collectedDate} - Receipt No: ${receipt.collectionApproval?.receiptNo || "N/A"}`;
        })
        .join("\n");

      const emiMonthsPaid = data.receiptsDetails
        ? new Set(data.receiptsDetails.filter(
          (receipt) => {
            if (!isValidPaidReceipt(receipt)) return false;
            if (!fromDate || !toDate) return true;
            const emiDate = new Date(receipt.emiDate);
            return emiDate >= new Date(fromDate) &&
              emiDate <= new Date(toDate);
          }
        ).map(receipt => receipt.emiDate)).size
        : 0;

      return {
        ...data,
        formattedEmiDateByBranchManager: emiDate,
        emiAmount,
        collectedEmiAmount,
        collectedDates,
        emiMonthsPaid,
      };
    });

    // A foreclosure settlement is recorded only in foreclosure_approval, never
    // as a receipts row (bugfix.md Bug 89), so it has no emiDate to match
    // against above and collectedEmiAmount never sees it. If the settlement
    // was approved and the Branch Manager's collection date falls inside the
    // requested range, add one extra row for it here - same pattern already
    // used for the Collection Report backend controllers.
    const approval = data?.[FORECLOSURE_APPROVAL_KEY];
    if (
      data.loanStatus?.toLowerCase() === "foreclosed" &&
      approval?.accountManagerStatus === "submitted" &&
      approval?.branchManagerStatusUpdatedAt
    ) {
      const settlementDate = new Date(approval.branchManagerStatusUpdatedAt);
      const inRange =
        (!fromDate || settlementDate >= new Date(fromDate)) &&
        (!toDate || settlementDate <= new Date(toDate));
      if (inRange) {
        result.push({
          ...data,
          formattedEmiDateByBranchManager: settlementDate,
          emiAmount: null,
          collectedEmiAmount: getForeclosureCashPrincipalCollected(data),
          collectedDates: `${settlementDate.toLocaleString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })} - Foreclosure Settlement`,
          emiMonthsPaid: 0,
        });
      }
    }

    return result;
  }

  return []; // Not disbursed or invalid input
};

export const convertDateStringToDMY = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};




export const formatToISTDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
  return new Intl.DateTimeFormat("en-GB", options).format(date);
};


export const getCurrentDateTimeWithOffset = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  // Get timezone offset in hours and minutes

  // Format date with time and timezone offset
  // return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${offsetSign}${offsetHours}:${offsetMinutes}`;
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

export const getCurrentDateInIST = () => {
  const options = {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
  return new Intl.DateTimeFormat("en-GB", options).format(new Date());
};

export const getCurrentTimeInIST = () => {
  const options = {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // 24-hour format (e.g., 14:30 instead of 2:30 PM)
  };
  return new Date().toLocaleTimeString("en-IN", options);
};

export const maskAadharNumber = (aadharNo) => {
  if (!aadharNo) return ""; // Handle undefined or null cases
  const aadharStr = String(aadharNo); // Convert to string if it's a number
  return `${aadharStr.slice(0, -4).replace(/\d/g, "*")}${aadharStr.slice(-4)}`;
};

export const getPendingEmiAmountForMember = (data) => {
  const emiDay = getEmiDay(data);
  const { firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
    data.branchManagerStatusUpdatedAt,
    data.proposedLoanDetails.tenureInMonths,
    emiDay,
    resolveEffectiveStartDate(data)
  );

  // Get EMI dates within the range
  const emiDates = getEmiDatesInRange(
    emiDay,
    firstEmiDate,
    new Date(),
    firstEmiDate,
    lastEmiDate
  );

  // Use unified disbursement amount
  const loanAmount = getDisbursementAmount(data);

  const emiAmount = calculateEmiAmount(
    loanAmount,
    data.proposedLoanDetails.rateOfInterest,
    data.proposedLoanDetails.tenureInMonths
  );

  const amountToReceive = emiDates.length * Math.round(emiAmount);
  const receiptsForMemberId = data.receiptsDetails.filter(
    (receipt) => (receipt.memberId === data.id || receipt.memberId === data.ApplicationId)
  );
  const totalReceivedAmount = receiptsForMemberId.reduce(
    (sum, receipt) => sum + (receipt.receivedAmount || 0),
    0
  );
  const pendingEmiAmount = amountToReceive - totalReceivedAmount;
  return Math.round(pendingEmiAmount);
};

export const getFutureEmiAmountWithMonths = (data) => {
  const isClosed = data.status === "Clear" || data.loanStatus?.toLowerCase() === "foreclosed" || data.loanStatus?.toLowerCase() === "completed";
  if (isClosed) return "0/0";

  const emiDay = getEmiDay(data);
  const { firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
    data.branchManagerStatusUpdatedAt,
    data.proposedLoanDetails.tenureInMonths,
    emiDay,
    resolveEffectiveStartDate(data)
  );

  // Get EMI dates within the range
  const emiDates = getEmiDatesInRange(
    emiDay,
    new Date(),
    lastEmiDate,
    firstEmiDate,
    lastEmiDate
  );

  const loanAmount = getDisbursementAmount(data);

  const emiAmount = calculateEmiAmount(
    loanAmount,
    data.proposedLoanDetails.rateOfInterest,
    data.proposedLoanDetails.tenureInMonths
  );

  const amountToReceive = emiDates.length * Math.round(emiAmount);
  return `${Math.round(amountToReceive)}/${emiDates.length}`;
};

export const getTotalEmiPaid = (data) => {
  if (!data || !data.receiptsDetails) return 0;
  const receiptsForMemberId = data.receiptsDetails.filter(
    (receipt) => (receipt.memberId === data.id || receipt.memberId === data.ApplicationId)
  );
  const totalReceivedAmount = receiptsForMemberId.reduce(
    (sum, receipt) => sum + (receipt.receivedAmount || 0),
    0
  );
  return Math.round(totalReceivedAmount);
};

export const getStatusOfMember = (data) => {
  // Priority 1: Check the processed status from generateLoanSummaryForMember if available
  if (data.status === "Foreclosed") return "Foreclosed";
  if (data.status === "Completed") return "Completed";
  if (data.status === "Clear") return "Clear";
  if (data.status === "Pending") return "Pending";

  // Priority 2: Fallback to raw loanStatus
  if (data.loanStatus?.toLowerCase() === "foreclosed") return "Foreclosed";
  if (data.loanStatus?.toLowerCase() === "completed") return "Completed";

  // Priority 3: Original tenure-based logic
  const tenureInMonths = data.proposedLoanDetails?.tenureInMonths || 0;
  if (tenureInMonths === 0) return "Active";

  const emiMonthsPaid = data.receiptsDetails
    ? new Set(
      data.receiptsDetails
        .filter((receipt) => receipt.status === "paid")
        .map((receipt) => receipt.emiDate)
    ).size
    : 0;
  return tenureInMonths <= emiMonthsPaid ? "Completed" : "Active";
};

export const handleFileChange = (e, name, setFilePreviews) => {
  const file = e.target.files[0];
  if (file) {
    const previewUrl = URL.createObjectURL(file);

    // Store both the file object and the preview URL
    setFilePreviews((prev) => ({
      ...prev,
      [name]: { file, previewUrl },
    }));
  }
};

export const initialFundingAgencyDetails = {
  agencyName: null,
  bcOrOwn: "OWN",
  fundingAgencyCode: null,
  // Business Loan fields
  blProcessingFeePercentage: 0,
  blProcessingFeeEnabled: true,
  blInsurancePercentage: 0,
  blInsuranceEnabled: true,
  blInsuranceAbove199999Percentage: 0,
  blInsuranceAbove199999Enabled: true,
  blSecurityDepositPercentage: 0,
  blSecurityDepositEnabled: true,
  blRevenueInterestPercentage: 0,
  blRevenueInterestEnabled: true,
  blGstPercentage: 0,
  blGstEnabled: true,
  blRateOfInterest: 0,
  blRateOfInterestEnabled: true,
  blRevenueProcessingFeePercentage: 0,
  blRevenueProcessingFeeEnabled: true,
  // JLG Loan fields
  jlgProcessingFeePercentage: 0,
  jlgProcessingFeeEnabled: true,
  jlgInsurancePercentage: 0,
  jlgInsuranceEnabled: true,
  jlgInsuranceAbove199999Percentage: 0,
  jlgInsuranceAbove199999Enabled: true,
  jlgSecurityDepositPercentage: 0,
  jlgSecurityDepositEnabled: true,
  jlgRevenueInterestPercentage: 0,
  jlgRevenueInterestEnabled: true,
  jlgGstPercentage: 0,
  jlgGstEnabled: true,
  jlgRateOfInterest: 0,
  jlgRateOfInterestEnabled: true,
  jlgRevenueProcessingFeePercentage: 0,
  jlgRevenueProcessingFeeEnabled: true,
  // GPL Loan fields
  gplProcessingFeePercentage: 0,
  gplProcessingFeeEnabled: true,
  gplInsurancePercentage: 0,
  gplInsuranceEnabled: true,
  gplInsuranceAbove199999Percentage: 0,
  gplInsuranceAbove199999Enabled: true,
  gplSecurityDepositPercentage: 0,
  gplSecurityDepositEnabled: true,
  gplRevenueInterestPercentage: 0,
  gplRevenueInterestEnabled: true,
  gplGstPercentage: 0,
  gplGstEnabled: true,
  gplRateOfInterest: 0,
  gplRateOfInterestEnabled: true,
  gplRevenueProcessingFeePercentage: 0,
  gplRevenueProcessingFeeEnabled: true,
  // Daily Loan (DL) fields. Zero rather than a borrowed figure: an agency that
  // has not been configured for DL must charge nothing, never quietly fall back
  // to another product's rates.
  dlRateOfInterest: 0,
  dlRateOfInterestEnabled: true,
  dlProcessingFeePercentage: 0,
  dlProcessingFeeEnabled: true,
  dlGstPercentage: 0,
  dlGstEnabled: true,
  dlSecurityDepositPercentage: 0,
  dlSecurityDepositEnabled: true,
  dlInsurancePercentage: 0,
  dlInsuranceEnabled: true,
  dlRevenueInterestPercentage: 0,
  dlRevenueInterestEnabled: true,
  dlRevenueProcessingFeePercentage: 0,
  dlRevenueProcessingFeeEnabled: true,
};

export const fundingAgencyDetailsFields = [
  {
    label: "Agency Name",
    type: "text",
    name: "agencyName",
    required: true,
  },
  {
    label: "BC/OWN",
    type: "select",
    name: "bcOrOwn",
    options: [
      { value: "BC", label: "BC" },
      { value: "OWN", label: "OWN" },
    ],
    required: true,
  },
  {
    label: "Funding Unique Id",
    type: "text",
    name: "fundingAgencyCode",
    required: false,
  },
  // Business Loan Fields
  {
    label: "BL Processing Fee %",
    type: "float",
    name: "blProcessingFeePercentage",
    required: false,
  },
  {
    label: "BL Processing Fee Enabled",
    type: "checkbox",
    name: "blProcessingFeeEnabled",
    required: false,
  },
  {
    label: "BL Insurance %",
    type: "float",
    name: "blInsurancePercentage",
    required: false,
  },
  {
    label: "BL Insurance Enabled",
    type: "checkbox",
    name: "blInsuranceEnabled",
    required: false,
  },
  {
    label: "BL Insurance Above ₹200000 %",
    type: "float",
    name: "blInsuranceAbove199999Percentage",
    required: false,
  },
  {
    label: "BL Insurance Above ₹200000 Enabled",
    type: "checkbox",
    name: "blInsuranceAbove199999Enabled",
    required: false,
  },
  {
    label: "BL Security Deposit %",
    type: "float",
    name: "blSecurityDepositPercentage",
    required: false,
  },
  {
    label: "BL Security Deposit Enabled",
    type: "checkbox",
    name: "blSecurityDepositEnabled",
    required: false,
  },
  {
    label: "BL Revenue Interest %",
    type: "float",
    name: "blRevenueInterestPercentage",
    required: false,
  },
  {
    label: "BL Revenue Interest Enabled",
    type: "checkbox",
    name: "blRevenueInterestEnabled",
    required: false,
  },
  {
    label: "BL Processing Fee GST %",
    type: "float",
    name: "blGstPercentage",
    required: false,
  },
  {
    label: "BL GST Enabled",
    type: "checkbox",
    name: "blGstEnabled",
    required: false,
  },
  {
    label: "BL Rate of Interest %",
    type: "float",
    name: "blRateOfInterest",
    required: false,
  },
  {
    label: "BL Rate of Interest Enabled",
    type: "checkbox",
    name: "blRateOfInterestEnabled",
    required: false,
  },
  {
    label: "BL Revenue Processing Fee %",
    type: "float",
    name: "blRevenueProcessingFeePercentage",
    required: false,
  },
  {
    label: "BL Revenue Processing Fee Enabled",
    type: "checkbox",
    name: "blRevenueProcessingFeeEnabled",
    required: false,
  },
  // JLG Loan Fields
  {
    label: "JLG Processing Fee %",
    type: "float",
    name: "jlgProcessingFeePercentage",
    required: false,
  },
  {
    label: "JLG Processing Fee Enabled",
    type: "checkbox",
    name: "jlgProcessingFeeEnabled",
    required: false,
  },
  {
    label: "JLG Insurance %",
    type: "float",
    name: "jlgInsurancePercentage",
    required: false,
  },
  {
    label: "JLG Insurance Enabled",
    type: "checkbox",
    name: "jlgInsuranceEnabled",
    required: false,
  },
  {
    label: "JLG Insurance Above ₹199999 %",
    type: "float",
    name: "jlgInsuranceAbove199999Percentage",
    required: false,
  },
  {
    label: "JLG Insurance Above ₹199999 Enabled",
    type: "checkbox",
    name: "jlgInsuranceAbove199999Enabled",
    required: false,
  },
  {
    label: "JLG Security Deposit %",
    type: "float",
    name: "jlgSecurityDepositPercentage",
    required: false,
  },
  {
    label: "JLG Security Deposit Enabled",
    type: "checkbox",
    name: "jlgSecurityDepositEnabled",
    required: false,
  },
  {
    label: "JLG Revenue Interest %",
    type: "float",
    name: "jlgRevenueInterestPercentage",
    required: false,
  },
  {
    label: "JLG Revenue Interest Enabled",
    type: "checkbox",
    name: "jlgRevenueInterestEnabled",
    required: false,
  },
  {
    label: "JLG Processing Fee GST %",
    type: "float",
    name: "jlgGstPercentage",
    required: false,
  },
  {
    label: "JLG GST Enabled",
    type: "checkbox",
    name: "jlgGstEnabled",
    required: false,
  },
  {
    label: "JLG Rate of Interest %",
    type: "float",
    name: "jlgRateOfInterest",
    required: false,
  },
  {
    label: "JLG Rate of Interest Enabled",
    type: "checkbox",
    name: "jlgRateOfInterestEnabled",
    required: false,
  },
  {
    label: "JLG Revenue Processing Fee %",
    type: "float",
    name: "jlgRevenueProcessingFeePercentage",
    required: false,
  },
  {
    label: "JLG Revenue Processing Fee Enabled",
    type: "checkbox",
    name: "jlgRevenueProcessingFeeEnabled",
    required: false,
  },
  // GPL Loan Fields
  {
    label: "GPL Processing Fee %",
    type: "float",
    name: "gplProcessingFeePercentage",
    required: false,
  },
  {
    label: "GPL Processing Fee Enabled",
    type: "checkbox",
    name: "gplProcessingFeeEnabled",
    required: false,
  },
  {
    label: "GPL Insurance %",
    type: "float",
    name: "gplInsurancePercentage",
    required: false,
  },
  {
    label: "GPL Insurance Enabled",
    type: "checkbox",
    name: "gplInsuranceEnabled",
    required: false,
  },
  {
    label: "GPL Insurance Above ₹200000 %",
    type: "float",
    name: "gplInsuranceAbove199999Percentage",
    required: false,
  },
  {
    label: "GPL Insurance Above ₹200000 Enabled",
    type: "checkbox",
    name: "gplInsuranceAbove199999Enabled",
    required: false,
  },
  {
    label: "GPL Security Deposit %",
    type: "float",
    name: "gplSecurityDepositPercentage",
    required: false,
  },
  {
    label: "GPL Security Deposit Enabled",
    type: "checkbox",
    name: "gplSecurityDepositEnabled",
    required: false,
  },
  {
    label: "GPL Revenue Interest %",
    type: "float",
    name: "gplRevenueInterestPercentage",
    required: false,
  },
  {
    label: "GPL Revenue Interest Enabled",
    type: "checkbox",
    name: "gplRevenueInterestEnabled",
    required: false,
  },
  {
    label: "GPL Processing Fee GST %",
    type: "float",
    name: "gplGstPercentage",
    required: false,
  },
  {
    label: "GPL GST Enabled",
    type: "checkbox",
    name: "gplGstEnabled",
    required: false,
  },
  {
    label: "GPL Rate of Interest %",
    type: "float",
    name: "gplRateOfInterest",
    required: false,
  },
  {
    label: "GPL Rate of Interest Enabled",
    type: "checkbox",
    name: "gplRateOfInterestEnabled",
    required: false,
  },
  {
    label: "GPL Revenue Processing Fee %",
    type: "float",
    name: "gplRevenueProcessingFeePercentage",
    required: false,
  },
  {
    label: "GPL Revenue Processing Fee Enabled",
    type: "checkbox",
    name: "gplRevenueProcessingFeeEnabled",
    required: false,
  },

  // Daily Loan (DL). These columns exist on funding_agencies but had no screen,
  // so the four charge percentages were NULL on every agency and DL charges
  // computed to zero. Purely additive: no BL, JLG or GPL field above is changed,
  // and renderConfigSection picks these up by the "dl" name prefix.
  //
  // Note DL has no "Insurance Above Rs.200000" pair. That threshold is a monthly
  // product rule; DL insurance is a flat percentage of the sanctioned amount
  // with no tenure scaling and no threshold.
  {
    label: "DL Rate Of Interest",
    type: "float",
    name: "dlRateOfInterest",
    required: false,
  },
  {
    label: "DL Rate Of Interest Enabled",
    type: "checkbox",
    name: "dlRateOfInterestEnabled",
    required: false,
  },
  {
    label: "DL Processing Fee %",
    type: "float",
    name: "dlProcessingFeePercentage",
    required: false,
  },
  {
    label: "DL Processing Fee Enabled",
    type: "checkbox",
    name: "dlProcessingFeeEnabled",
    required: false,
  },
  {
    label: "DL GST %",
    type: "float",
    name: "dlGstPercentage",
    required: false,
  },
  {
    label: "DL GST Enabled",
    type: "checkbox",
    name: "dlGstEnabled",
    required: false,
  },
  {
    label: "DL Security Deposit %",
    type: "float",
    name: "dlSecurityDepositPercentage",
    required: false,
  },
  {
    label: "DL Security Deposit Enabled",
    type: "checkbox",
    name: "dlSecurityDepositEnabled",
    required: false,
  },
  {
    label: "DL Insurance %",
    type: "float",
    name: "dlInsurancePercentage",
    required: false,
  },
  {
    label: "DL Insurance Enabled",
    type: "checkbox",
    name: "dlInsuranceEnabled",
    required: false,
  },
  {
    label: "DL Revenue Interest %",
    type: "float",
    name: "dlRevenueInterestPercentage",
    required: false,
  },
  {
    label: "DL Revenue Interest Enabled",
    type: "checkbox",
    name: "dlRevenueInterestEnabled",
    required: false,
  },
  {
    label: "DL Revenue Processing Fee %",
    type: "float",
    name: "dlRevenueProcessingFeePercentage",
    required: false,
  },
  {
    label: "DL Revenue Processing Fee Enabled",
    type: "checkbox",
    name: "dlRevenueProcessingFeeEnabled",
    required: false,
  },
];

export const initialDailyGoldrate = {
  rateDate: null,
  goldRate22k: null,
};

export const dailyGoldRateDetailsFields = [
  {
    label: "Date",
    type: "Date",
    name: "rateDate",
    required: true,
  },
  {
    label: "Rate",
    type: "float",
    name: "goldRate22k",
    required: true,
  },
];

// The association alias attachForeclosureWaivers.js (backend) attaches the
// foreclosure_approval row under, on every Master report row that has one.
const FORECLOSURE_APPROVAL_KEY =
  "fk_member_details_hasOne_member_foreclosure_approval_memberId";

// The principal actually recovered at foreclosure - separate from the 4%
// charge and 18% GST, which are a fee, not loan repayment. Mirrors the
// backend's getForeclosurePrincipalRecovered (backend/utils/foreclosureSettlements.js)
// exactly; kept as its own copy here because the frontend can't import
// backend code, the same reason dlCalculations.js exists in three copies.
// Used because a foreclosure settlement is recorded only in
// foreclosure_approval, never as a receipts row (bugfix.md Bug 89), so
// principalPaid/totalEmiPaid below - built purely from receiptsDetails -
// never see it without this addition.
// Principal CLEARED by the foreclosure, including a security deposit deducted
// from the payable amount - for "is the loan paid off" figures (Master,
// Outstanding, Account Statement).
function getForeclosurePrincipalRecovered(data) {
  return foreclosurePrincipalFrom(data, true);
}

// Principal paid in CASH on the foreclosure day, without the deposit (collected
// at disbursement) - for the Collection report, which must match the cash received.
function getForeclosureCashPrincipalCollected(data) {
  return foreclosurePrincipalFrom(data, false);
}

function foreclosurePrincipalFrom(data, includeDeposit) {
  const approval = data?.[FORECLOSURE_APPROVAL_KEY];
  if (!approval || approval.accountManagerStatus !== "submitted") return 0;
  const outstanding = Number(approval.totalOutstandingAmount) || 0;
  const charges = Number(approval.forecloseChargesAmount) || 0;
  const gst = Number(approval.forecloseGstAmount) || 0;
  const depositUsed =
    includeDeposit && approval.isSecurityDepositAdjusted
      ? Number(approval.securityDepositAmount) || 0
      : 0;
  const collected =
    approval.collectedAmount != null
      ? Number(approval.collectedAmount) + depositUsed
      : outstanding + charges + gst;
  return Math.max(0, Math.min(outstanding, collected - charges - gst));
}

const MONTHLY_APPROVAL_KEY = {
  "Business Loan": "fk_receipts_hasOne_bl_collection_approval_receiptId",
  "GP Loan": "fk_receipts_hasOne_gpl_collection_approval_receiptId",
};

const toPortfolioBucket = (dpd) => {
  if (dpd > 0 && dpd <= 30) return "0-30";
  if (dpd > 30 && dpd <= 60) return "31-60";
  if (dpd > 60 && dpd <= 90) return "61-90";
  if (dpd > 90 && dpd <= 180) return "91-180";
  if (dpd > 180 && dpd <= 365) return "181-365";
  if (dpd > 365) return ">365";
  return "0";
};

// Business Loan and GP Loan both repay monthly, and money is allocated to the
// oldest instalment first, interest before principal (loanAllocation.js). They
// differ only in input validation and how the charges are read.
function buildMonthlyLoanSummary(data, { isClosed, firstEmiDate, lastEmiDate, toDate }) {
  const isBl = data.loanType === "Business Loan";
  // Shared with the backend foreclosure quote (monthlyLoanTerms.js), so both always agree.
  const terms = getMonthlyLoanTerms(data);
  if (!terms) return null;
  const { loanAmount, annualRatePercent: rateOfInterest, tenure: tenureInMonths, rawEmiAmount, emiAmount } = terms;

  const today = toIstDateOnly(new Date());
  const requested = toIstDateOnly(toDate) || today;
  const asAtDate = requested < today ? requested : today;

  const approvalKey = MONTHLY_APPROVAL_KEY[data.loanType];
  const receipts = Array.isArray(data.receiptsDetails) ? data.receiptsDetails : [];
  const allocation = summarizeLoanAllocation({
    loanAmount,
    annualRatePercent: rateOfInterest,
    tenure: tenureInMonths,
    emiAmount,
    firstDueDate: terms.firstDueDate,
    dueDay: terms.dueDay,
    receipts,
    getApproval: (r) => r[approvalKey],
    asAtDate,
  });
  if (!allocation) return null;

  // A receipt can pay more than one instalment. `receipts` lists it once, under the
  // first instalment it paid; `allocations` shows the part it paid of every
  // instalment, marked carriedForward after the first (statementRows.js).
  const receiptById = new Map(receipts.map((r) => [r.id, r]));
  const listed = new Set();
  const schedule = allocation.instalments.map((inst) => {
    const own = inst.receiptIds.filter((id) => !listed.has(id));
    const allocations = inst.allocations.map((a) => ({
      receipt: receiptById.get(a.receiptId),
      interest: a.interest,
      principal: a.principal,
      carriedForward: listed.has(a.receiptId),
    }));
    own.forEach((id) => listed.add(id));
    return {
      number: inst.number,
      emiDate: inst.dueDate,
      interestCharge: inst.interest,
      principalCharge: inst.principal,
      paidInterest: inst.paidInterest,
      paidPrincipal: inst.paidPrincipal,
      totalReceivedAmount: inst.paid,
      receipts: own.map((id) => receiptById.get(id)),
      allocations,
      status: inst.status,
      isDue: inst.isDue,
    };
  });
  // Money no instalment needed yet: a whole receipt, or the rest of one that paid earlier instalments.
  allocation.leftovers.forEach(({ receiptId, amount }) => {
    const r = receiptById.get(receiptId);
    schedule.push({
      emiDate: null,
      actualDate: r.collectedDate || r.emiDate,
      interestCharge: 0,
      totalReceivedAmount: amount,
      receipts: [r],
      status: "Advance Payment",
      isAdvance: true,
      carriedForward: listed.has(receiptId),
    });
    listed.add(receiptId);
  });

  let lastPaidDate = null;
  if (isClosed && data.loanCloseDate) {
    lastPaidDate = data.loanCloseDate;
  } else {
    const counted = [...listed].map((id) => receiptById.get(id));
    counted.sort((a, b) => new Date(a.collectedDate || a.emiDate) - new Date(b.collectedDate || b.emiDate));
    const latest = counted[counted.length - 1];
    lastPaidDate = latest ? latest.collectedDate || latest.emiDate : null;
  }

  let principalPaid = allocation.principalPaid;
  let totalEmiPaid = allocation.totalPaid + allocation.excess;
  const isForeclosed = data.loanStatus?.toLowerCase() === "foreclosed";
  if (isClosed && isForeclosed) {
    const settledPrincipal = getForeclosurePrincipalRecovered(data);
    principalPaid += settledPrincipal;
    totalEmiPaid += settledPrincipal;
  }

  const insuranceAmount =
    !isBl && data.insuranceAmount !== undefined
      ? formatAmount(data.insuranceAmount)
      : calculateInsuranceAmount(loanAmount, tenureInMonths);
  const securityDeposit =
    !isBl && data.securityDeposit !== undefined ? formatAmount(data.securityDeposit) : Math.round(rawEmiAmount);
  const shortfall = Math.round(allocation.shortfall);
  const dpd = allocation.dpd;

  return {
    ...data,
    totalOutstandingPrincipal: isClosed ? 0 : Math.max(0, Math.round(allocation.outstandingPrincipal)),
    totalOutstandingInterest: isClosed ? 0 : Math.max(0, Math.round(allocation.outstandingInterest)),
    principalPaid: Math.max(0, Math.round(principalPaid)),
    interestPaid: Math.max(0, Math.round(allocation.interestPaid)),
    totalEmiPaid: Math.max(0, Math.round(totalEmiPaid)),
    emiMonthsPaid: allocation.instalmentsFullyCleared,
    insuranceAmount,
    processingCharge: formatAmount(data.processingCharge || 0),
    gst: formatAmount(data.gst || 0),
    securityDeposit,
    totalPendingAmount: isClosed ? 0 : shortfall,
    status: isClosed ? (isForeclosed ? "Foreclosed" : "Completed") : shortfall > 0 ? "Pending" : "Clear",
    overdueDays: isClosed ? 0 : dpd,
    dpd: isClosed ? 0 : dpd,
    bucket: isClosed ? "0" : toPortfolioBucket(dpd),
    overduePrincipal: isClosed ? 0 : Math.max(0, Math.round(allocation.overduePrincipal)),
    overdueInterest: isClosed ? 0 : Math.max(0, Math.round(allocation.overdueInterest)),
    totalPAR: isClosed ? 0 : dpd > 0 ? Math.max(0, Math.round(allocation.outstandingPrincipal)) : 0,
    lastPaidDate: lastPaidDate ? toLocalYmd(new Date(lastPaidDate)) : null,
    firstOverdueDate: isClosed ? null : allocation.oldestUnpaidDueDate,
    emiAmount,
    totalLoanInterest: allocation.instalments.reduce((s, i) => s + i.interest, 0),
    firstEmiDate,
    lastEmiDate,
    isSettled: isClosed,
    schedule,
  };
}


export function generateLoanSummaryForMember(data, fromDate, toDate) {
  if (
    data.branchManagerStatus !== "disbursed" ||
    !data.branchManagerStatusUpdatedAt
  ) {
    return null;
  }

  const rawCloseDate = data.loanCloseDate ? new Date(data.loanCloseDate) : null;
  const isPermanentlyClosed = data.status === "Closed" || data.loanStatus?.toLowerCase() === "foreclosed" || data.loanStatus?.toLowerCase() === "completed";
  
  let isClosed = false;
  if (isPermanentlyClosed) {
    if (!toDate) {
      isClosed = true;
    } else {
      const reportDateStr = toLocalYmd(new Date(toDate));
      if (rawCloseDate && !isNaN(rawCloseDate.getTime())) {
        const closeDateStr = toLocalYmd(rawCloseDate);
        isClosed = reportDateStr >= closeDateStr;
      } else {
        isClosed = true;
      }
    }
  }

  let firstEmiDate, lastEmiDate, emiDates;

    const emiDay = getEmiDay(data);
    if (data.loanType === "Business Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "JLG Loan") {
      ({ firstEmiDate, lastEmiDate } = getJlgFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
      ));
      emiDates = getJlgEmiDatesInRange(
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else if (data.loanType === "GP Loan") {
      ({ firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
        data.branchManagerStatusUpdatedAt,
        data.proposedLoanDetails.tenureInMonths,
        emiDay,
        resolveEffectiveStartDate(data)
      ));
      emiDates = getEmiDatesInRange(
        emiDay,
        fromDate,
        toDate,
        firstEmiDate,
        lastEmiDate
      );
    } else {
    return null;
  }

  // --- LOGIC BRANCHING START ---
  if (data.loanType === "Business Loan" || data.loanType === "GP Loan") {
    return buildMonthlyLoanSummary(data, { isClosed, firstEmiDate, lastEmiDate, toDate });
  } else {
    // --- ORIGINAL LOGIC FOR JLG AND OTHER LOANS ---
    // Was reading sanctionedLoanAmountBySanctionCommittee directly. When that
    // column is empty - as it is on a loan that never passed the sanction
    // committee step - parseFloat gave NaN, the guard below returned null, and
    // the screen rendered the loan as Rs.0 outstanding, hiding real dues.
    //
    // This is the same failure that was fixed for GP loans in bugfix.md Bug 4;
    // the Business Loan and GP Loan branches already call getDisbursementAmount,
    // which falls back through the other amount columns. This branch was missed.
    const sanctionedLoanAmount = getDisbursementAmount(data);

    const loanAmount = parseFloat(sanctionedLoanAmount);
    const rateOfInterest = parseFloat(data.proposedLoanDetails?.rateOfInterest);
    const tenureInMonths = parseInt(data.proposedLoanDetails?.tenureInMonths);

    if (isNaN(loanAmount) || isNaN(rateOfInterest) || isNaN(tenureInMonths)) return null;

    const rawEmiAmount = getEffectiveEmi(data);
    if (isNaN(rawEmiAmount) || rawEmiAmount <= 0) return null;

    const emiAmount = Math.round(rawEmiAmount);
    const totalLoanInterest = calculateTotalLoanInterest(loanAmount, rateOfInterest, tenureInMonths);

    let totalOutstandingPrincipal = Math.round(loanAmount);
    let totalOutstandingInterest = Math.round(totalLoanInterest);
    let principalPaid = 0;
    let interestPaid = 0;
    let totalEmiPaid = 0;

    let firstPendingEmiDate = null;
    let lastPaidDate = null;
    let history = [];
    const receiptsDetails = Array.isArray(data.receiptsDetails) ? data.receiptsDetails : [];

    const allPaidReceiptsSorted = [...receiptsDetails]
      .filter(r =>
        // Single validator - see isValidPaidReceipt in this file.
        isValidPaidReceiptWithApproval(r, r.fk_receipts_hasOne_jlg_collection_approval_receiptId)
      )
      .sort((a, b) => new Date(a.collectedDate || a.emiDate) - new Date(b.collectedDate || b.emiDate));

    lastPaidDate = null;
    if (isClosed && data.loanCloseDate) {
      lastPaidDate = new Date(data.loanCloseDate);
    } else if (allPaidReceiptsSorted.length > 0) {
      const latestReceipt = allPaidReceiptsSorted[allPaidReceiptsSorted.length - 1];
      const lpDateRaw = latestReceipt.collectedDate || latestReceipt.emiDate;
      lastPaidDate = lpDateRaw ? new Date(lpDateRaw) : null;
    }

    const totalMemberPaidAmount = allPaidReceiptsSorted.reduce((sum, r) => sum + (parseFloat(String(r.receivedAmount).replace(/,/g, "")) || 0), 0);
    let runningCumulativeExpected = 0;

    const benchmarkDate = (toDate && !isNaN(new Date(toDate).getTime())) ? new Date(toDate) : new Date();
    const comparisonDate = new Date(benchmarkDate.getFullYear(), benchmarkDate.getMonth(), benchmarkDate.getDate(), 23, 59, 59);

    emiDates.forEach((emiDate) => {
      const emiDateStr = toLocalYmd(emiDate);
      const currentOutstandingPrincipal = Math.max(0, totalOutstandingPrincipal);
      const interestCharge = Math.round((currentOutstandingPrincipal * rateOfInterest) / 12 / 100);
      const expectedForThisMonth = Math.min(emiAmount, Math.round(totalOutstandingPrincipal + interestCharge));

      const receiptsForThisEmi = allPaidReceiptsSorted.filter(r => {
        const rDateNormal = typeof r.emiDate === 'string' ? toLocalYmd(new Date(r.emiDate)) : toLocalYmd(r.emiDate);
        return rDateNormal === emiDateStr;
      });
      const receivedThisEmi = Math.round(receiptsForThisEmi.reduce((sum, r) => sum + (parseFloat(String(r.receivedAmount).replace(/,/g, "")) || 0), 0));

      let remainingToSplit = receivedThisEmi;
      const actualInterestPaid = Math.min(interestCharge, remainingToSplit);
      interestPaid += actualInterestPaid;
      totalOutstandingInterest = Math.max(0, totalOutstandingInterest - actualInterestPaid);
      remainingToSplit -= actualInterestPaid;

      const actualPrincipalPaid = Math.min(Math.round(totalOutstandingPrincipal), remainingToSplit);
      principalPaid += actualPrincipalPaid;
      totalOutstandingPrincipal = Math.max(0, totalOutstandingPrincipal - actualPrincipalPaid);

      totalEmiPaid += receivedThisEmi;

      if (emiDate <= comparisonDate) {
        runningCumulativeExpected += expectedForThisMonth;
        if (!firstPendingEmiDate && totalMemberPaidAmount < (runningCumulativeExpected - 1)) {
          firstPendingEmiDate = emiDate;
        }
      }

      history.push({
        emiDate: emiDateStr,
        interestCharge: interestCharge,
        totalReceivedAmount: receivedThisEmi,
        receipts: receiptsForThisEmi,
        status: receivedThisEmi >= expectedForThisMonth ? "Paid" : receivedThisEmi > 0 ? "Partial" : "Unpaid"
      });
    });

    const totalPaidRounded = Math.round(allPaidReceiptsSorted.reduce((sum, r) => sum + (parseFloat(String(r.receivedAmount).replace(/,/g, "")) || 0), 0));
    const netShortfall = Math.max(0, Math.round(runningCumulativeExpected - totalPaidRounded));
    let finalOverduePrincipal = 0;
    let finalOverdueInterest = 0;
    let finalOverdueDays = 0;

    if (netShortfall > 10) {
      if (firstPendingEmiDate) {
        const timeDiff = comparisonDate - firstPendingEmiDate;
        finalOverdueDays = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60 * 24)));
      }

      let totalInterestDueSoFar = 0;
      let tempPrincipal = loanAmount;
      emiDates.forEach(d => {
        if (d <= comparisonDate) {
          const mInt = Math.round((tempPrincipal * rateOfInterest) / 12 / 100);
          totalInterestDueSoFar += mInt;
          const mEmi = Math.min(emiAmount, Math.round(tempPrincipal + mInt));
          tempPrincipal -= (mEmi - mInt);
        }
      });

      const totalInterestShortfall = Math.max(0, totalInterestDueSoFar - interestPaid);
      finalOverdueInterest = Math.min(netShortfall, totalInterestShortfall);
      finalOverduePrincipal = netShortfall - finalOverdueInterest;
    } else {
      firstPendingEmiDate = null;
      finalOverdueDays = 0;
      finalOverduePrincipal = 0;
      finalOverdueInterest = 0;
    }

    let bucket = "0";
    if (finalOverdueDays > 0 && finalOverdueDays <= 30) bucket = "0-30";
    else if (finalOverdueDays > 30 && finalOverdueDays <= 60) bucket = "31-60";
    else if (finalOverdueDays > 60 && finalOverdueDays <= 90) bucket = "61-90";
    else if (finalOverdueDays > 90 && finalOverdueDays <= 180) bucket = "91-180";
    else if (finalOverdueDays > 180 && finalOverdueDays <= 365) bucket = "181-365";
    else if (finalOverdueDays > 365) bucket = ">365";

    if (isClosed && data.loanStatus?.toLowerCase() === "foreclosed") {
      const settledPrincipal = getForeclosurePrincipalRecovered(data);
      principalPaid += settledPrincipal;
      totalEmiPaid += settledPrincipal;
    }

    return {
      ...data,
      totalOutstandingPrincipal: isClosed ? 0 : Math.max(0, Math.round(totalOutstandingPrincipal)),
      totalOutstandingInterest: isClosed ? 0 : Math.max(0, Math.round(totalOutstandingInterest)),
      principalPaid: Math.max(0, Math.round(principalPaid)),
      interestPaid: Math.max(0, Math.round(interestPaid)),
      totalEmiPaid: Math.round(totalEmiPaid),
      totalPendingAmount: isClosed ? 0 : netShortfall,
      status: isClosed ? (data.loanStatus?.toLowerCase() === "foreclosed" ? "Foreclosed" : "Completed") : (netShortfall > 0 ? "Pending" : "Clear"),
      overdueDays: isClosed ? 0 : finalOverdueDays,
      dpd: isClosed ? 0 : finalOverdueDays,
      bucket: isClosed ? "0" : bucket,
      overduePrincipal: isClosed ? 0 : Math.max(0, Math.round(finalOverduePrincipal)),
      overdueInterest: isClosed ? 0 : Math.max(0, Math.round(finalOverdueInterest)),
      totalPAR: isClosed ? 0 : (finalOverdueDays > 0 ? Math.max(0, Math.round(totalOutstandingPrincipal)) : 0),
      lastPaidDate: lastPaidDate ? toLocalYmd(lastPaidDate) : null,
      firstOverdueDate: isClosed ? null : (firstPendingEmiDate ? toLocalYmd(firstPendingEmiDate) : null),
      emiAmount: emiAmount,
      totalLoanInterest: (emiAmount * tenureInMonths) - loanAmount,
      firstEmiDate,
      lastEmiDate,
      isSettled: isClosed,
      schedule: history
    };
  }
}




export const calculateTotalLoanInterest = (principal, annualRate, months) => {
  const { emi } = rawMonthlyEmi(principal, annualRate, months);
  return emi * months - principal;
};

export const fetchGoldRate = async () => {
  try {
    const todayDate = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    const response = await axios.get(
      `${BASE_URL}/api/getModelData/daily_gold_rate?filter={"rateDate":"${todayDate}"}`
    );
    const data = await response.data.data[0];
    return data.goldRate22k;
  } catch (error) {
    console.error("Error fetching gold rate:", error);
    return 0; // Return default value if fetch fails
  }
};

export function formatCurrency(value) {
  if (value === undefined || value === null || isNaN(value)) return 0;
  return parseFloat(Number(value).toFixed(2));
}
